// TUT-465 — no celular, o menu vira BARRA INFERIOR (pedido do redesenho
// "uau", TUT-463: "menu vira barra inferior", botões de no mínimo 44px).
//
// Quatro atalhos e o "Mais", que abre a gaveta com o menu inteiro — a mesma
// `complementary` "Navegação principal" de sempre, de onde a e2e navega. Os
// nomes são os do sistema, sem abreviar (dono, 02/10: "não pode mudar os nomes
// dos botões"): cada um cabe em até duas linhas, quebrando só entre palavras.
import { Menu } from "lucide-react";
import { Link, useMatchRoute } from "@tanstack/react-router";
import { useEffect, useLayoutEffect, useRef, useState, type ComponentProps } from "react";
import { useSidebar } from "@/components/ui/sidebar";
import type { EntradaDeNavegacao } from "@/components/app-nav";
import { cn } from "@/lib/utils";

/** Os atalhos, nesta ordem; quem a sessão não alcança simplesmente não entra. */
const ATALHOS = ["/", "/agenda", "/processos", "/elaboracao"] as const;

/**
 * Cada botão tem a largura do próprio nome (mínimo 44px) e divide a sobra com
 * os outros — com largura igual, "Elaboração" não cabia em 360 px.
 */
const itemClass =
  "flex min-h-[3.25rem] min-w-11 flex-1 basis-auto flex-col items-center justify-center gap-0.5 rounded-[var(--radius-bloco)] px-1 py-1.5 text-center " +
  "text-legenda leading-tight font-semibold text-[var(--sidebar-muted)] outline-none " +
  "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 focus-visible:ring-sidebar-ring " +
  "[&>span]:line-clamp-2 [&>span]:max-w-full [&>span]:break-normal";
const ativoClass =
  "bg-sidebar-primary text-sidebar-primary-foreground hover:bg-sidebar-primary hover:text-sidebar-primary-foreground";

export function BarraInferior({ navegacao }: { navegacao: readonly EntradaDeNavegacao[] }) {
  const { isMobile, setOpenMobile } = useSidebar();
  const matchRoute = useMatchRoute();
  // A barra é fixa e cobre o rodapé da tela. Sem reserva, o foco de teclado
  // e o `scrollIntoView` (inclusive o da e2e) paravam o alvo ATRÁS dela — a
  // WCAG 2.4.11 (foco não encoberto). O `scroll-padding-bottom` do documento
  // faz toda rolagem até um alvo parar acima da barra, enquanto ela existe.
  useLayoutEffect(() => {
    if (!isMobile) return;
    const raiz = document.documentElement;
    const antes = raiz.style.scrollPaddingBottom;
    raiz.style.scrollPaddingBottom = "calc(5.5rem + env(safe-area-inset-bottom))";
    return () => {
      raiz.style.scrollPaddingBottom = antes;
    };
  }, [isMobile]);
  // Some ao rolar para BAIXO e volta ao rolar para cima (ou no foco de
  // teclado): é o padrão dos apps de celular, e é o que impede a barra de
  // ficar na frente do que a pessoa está indo ver. Medido na e2e: fixa o
  // tempo todo, ela interceptava o toque em nove jornadas, porque o alvo
  // "já visível" atrás dela não pede rolagem nenhuma.
  const [escondida, setEscondida] = useState(false);
  const barraRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!isMobile) return;
    // O foco de teclado chegando na barra a traz de volta.
    const barra = barraRef.current;
    const mostrar = () => setEscondida(false);
    barra?.addEventListener("focusin", mostrar);
    let ultimo = window.scrollY;
    const aoRolar = () => {
      const agora = window.scrollY;
      if (Math.abs(agora - ultimo) < 6) return;
      setEscondida(agora > ultimo && agora > 48);
      ultimo = agora;
    };
    window.addEventListener("scroll", aoRolar, { passive: true });
    return () => {
      window.removeEventListener("scroll", aoRolar);
      barra?.removeEventListener("focusin", mostrar);
    };
  }, [isMobile]);
  if (!isMobile) return null;
  const itens = ATALHOS.flatMap((caminho) => {
    const entrada = navegacao.find((item) => item.to === caminho);
    return entrada ? [entrada] : [];
  });
  return (
    <nav
      aria-label="Atalhos de navegação"
      data-shell="barra-inferior"
      ref={barraRef}
      data-escondida={escondida ? "true" : undefined}
      className={cn(
        "fixed inset-x-3 bottom-[calc(0.6rem+env(safe-area-inset-bottom))] z-30 flex gap-1 rounded-[var(--radius-vidro)] border border-sidebar-border bg-sidebar p-1 text-sidebar-foreground shadow-[var(--sidebar-sombra)]",
        "transition-transform duration-[var(--dur-slow)] ease-[var(--ease-out)] motion-reduce:transition-none",
        // Escondida, sai da tela e não recebe toque no caminho.
        escondida && "pointer-events-none translate-y-[calc(100%+1.5rem)]",
      )}
    >
      {itens.map(({ to, label, icon: Icone }) => {
        const destino = to as ComponentProps<typeof Link>["to"];
        const ativo = Boolean(matchRoute({ to: destino, fuzzy: to !== "/" }));
        return (
          <Link
            key={to}
            to={destino}
            aria-current={ativo ? "page" : undefined}
            className={cn(itemClass, ativo && ativoClass)}
          >
            <Icone aria-hidden="true" className="flex-none" />
            <span>{label}</span>
          </Link>
        );
      })}
      <button
        type="button"
        // A gaveta é diálogo modal: abre por cima e prende o foco, então o
        // "aberto" quem anuncia é ela — o botão só diz que abre um diálogo.
        aria-haspopup="dialog"
        onClick={() => setOpenMobile(true)}
        className={itemClass}
      >
        <Menu aria-hidden="true" className="flex-none" />
        <span>Mais</span>
      </button>
    </nav>
  );
}

/** O respiro de baixo do conteúdo, para a barra não cobrir o fim da página. */
export const respiroDaBarraInferior =
  "max-[980px]:pb-[calc(5.5rem+env(safe-area-inset-bottom))]";
